import { ChevronDownIcon, ChevronRightIcon, ExclamationTriangleIcon, MapIcon } from "@heroicons/react/20/solid";
import type { ReactNode } from "react";
import { OtsAvatar } from "../components/OtsAvatar";
import { OtsBadge } from "../components/OtsBadge";
import { OtsIconActionButton } from "../components/OtsButton";
import { OtsCheckbox } from "../components/OtsCheckbox";
import { OtsTooltip } from "../components/OtsTooltip";
import { formatActivityDuration, formatActivityMoney, formatActivityQuantity } from "./driver-activity-format";
import { activityMutedClass } from "./driver-activity-layout";
import type { ActivityDriver } from "./driver-activity-view-model";

export type ActivitySelectionState = "none" | "some" | "all";

export type ActivityDriverHeaderProps = {
  driver: ActivityDriver;
  collapsed?: boolean;
  onToggle?: () => void;
  showRevenue?: boolean;
  /** How many of the driver's tickets are selected; with `onToggleSelection` this adds a select-all checkbox. */
  selection?: ActivitySelectionState;
  onToggleSelection?: () => void;
  /** Shown before the totals, e.g. the driver's weakest audit. */
  audit?: ReactNode;
  /** The driver's day timeline, shown between the name and the totals. */
  timeline?: ReactNode;
  /** Opens the driver's whole day on a map. */
  onOpenMap?: () => void;
  className?: string;
};

/** A driver's name and day totals; doubles as the expand/collapse and select-all control for their activity. */
export const ActivityDriverHeader = ({
  driver,
  collapsed = false,
  onToggle,
  showRevenue = false,
  selection = "none",
  onToggleSelection,
  audit,
  timeline,
  onOpenMap,
  className,
}: ActivityDriverHeaderProps) => {
  const { totals } = driver;
  const Chevron = collapsed ? ChevronRightIcon : ChevronDownIcon;
  const strong = "text-[var(--ots-text)]";

  return (
    <div
      role="row"
      className={[
        "border-b border-[var(--ots-outline)] bg-[var(--ots-surface-variant)] bg-linear-to-r from-[var(--ots-primary-container)] to-[var(--ots-surface-variant)] to-50%",
        className,
      ].filter(Boolean).join(" ")}>
      <div role="gridcell" className="flex items-center gap-3 py-2 pl-2.5 pr-3">
        {onToggleSelection && totals.loads > 0 && (
          <OtsCheckbox
            aria-label={`Select all of ${driver.name}'s tickets`}
            checked={selection === "all"}
            indeterminate={selection === "some"}
            onChange={onToggleSelection}/>
        )}
        <OtsIconActionButton
          variant="text"
          size="compact"
          icon={<Chevron/>}
          label={`${collapsed ? "Expand" : "Collapse"} ${driver.name}`}
          aria-expanded={!collapsed}
          onClick={onToggle}/>
        <span className="flex min-w-0 shrink-0 items-center gap-2">
          <OtsAvatar name={driver.name} size="sm" decorative/>
          <span className="min-w-0">
            <span className="block truncate font-[family-name:var(--ots-font-display)] text-sm font-semibold">{driver.name}</span>
            {driver.companyName && <span className={["block truncate text-xs", activityMutedClass].join(" ")}>{driver.companyName}</span>}
          </span>
        </span>
        {onOpenMap && (
          <OtsIconActionButton variant="text" size="compact" icon={<MapIcon/>} label={`Map ${driver.name}'s day`} onClick={onOpenMap}/>
        )}
        {timeline != null && <div className="mx-2 hidden min-w-0 max-w-xl flex-1 @4xl:block">{timeline}</div>}
        <span className={["ml-auto flex shrink-0 flex-wrap items-center justify-end gap-x-4 gap-y-1 text-xs tabular-nums", activityMutedClass].join(" ")}>
          {audit}
          {totals.attention > 0 && <OtsBadge tone="warning" size="compact" icon={<ExclamationTriangleIcon/>}>{totals.attention} to review</OtsBadge>}
          {totals.shifts > 1 && <span><strong className={strong}>{totals.shifts}</strong> shifts</span>}
          {totals.loads > 0 && <>
            <span><strong className={strong}>{totals.loads}</strong> {totals.loads === 1 ? "load" : "loads"}</span>
            <span><strong className={strong}>{formatActivityQuantity(totals.quantity)}</strong> {totals.quantityUom}</span>
          </>}
          {totals.shifts > 0 && <span><strong className={strong}>{formatActivityDuration(totals.workedMillis)}</strong> on shift</span>}
          {showRevenue && totals.loads > 0 && (
            <OtsTooltip density="compact" content="Revenue / cost">
              <span tabIndex={0} className="rounded outline-none focus-visible:ring-2 focus-visible:ring-[var(--ots-primary)]">
                <strong className={strong}>{formatActivityMoney(totals.revenue)}</strong>
                {totals.cost > 0 && <> / {formatActivityMoney(totals.cost)}</>}
              </span>
            </OtsTooltip>
          )}
        </span>
      </div>
    </div>
  );
};
